import * as Sentry from '@sentry/vue';
import { ofetch } from 'ofetch';
import router from '@/router';
import getConfig from '@/utils/config';
import { TokenManager } from '@/utils/token_manager';
import { refreshTokenIfNeeded } from '@/plugins/msal';
import { captureApiError } from '@/utils/sentry-context';

const mxbApiBaseURL: string = getConfig('VITE_APP_API_BASE_URL');
const mxbBaseURL = mxbApiBaseURL.includes('localhost')
  ? `${mxbApiBaseURL}/v1`
  : `${mxbApiBaseURL}/api/v1`;

export const $mxbFetchWithoutPreSignIn = ofetch.create({
  baseURL: mxbBaseURL,
  async onRequest({ options }) {
    const accessToken = TokenManager.getCachedToken('access_token');

    options.headers = {
      ...options.headers,
      Authorization: `Bearer ${accessToken?.token}`,
      'Content-Type': 'application/json'
    };
  },

  async onResponseError({ request, options, error, response }) {
    captureApiError({
      errorName: '[$mxbFetchWithoutPreSignIn]: API response error',
      request,
      response,
      error,
      options,
      method: options?.method,
      endpoint: String(request)
    });

    if (response?.status === 401) {
      Sentry.logger.warn('Unauthorized API request', {
        log_source: 'fetch_error',
        endpoint: String(request),
        method: options?.method || 'GET'
      });
      router.push('/unauthorized');
    }

    if (response?.status === 403) {
      Sentry.logger.warn('Forbidden API request', {
        log_source: 'fetch_error',
        endpoint: String(request),
        method: options?.method || 'GET'
      });
      router.push('/forbidden');
    }
  }
});

export const $mxbFetch = ofetch.create({
  baseURL: mxbBaseURL,

  async onRequest({ options }) {
    const accessToken = TokenManager.getCachedToken('access_token');

    options.headers = {
      ...options.headers,
      Authorization: `Bearer ${accessToken?.token}`,
      'Content-Type': 'application/json'
    };
  },

  async onResponseError({ request, options, error, response }) {
    captureApiError({
      errorName: '[$mxbFetch]: API response error',
      request,
      response,
      error,
      options,
      method: options?.method,
      endpoint: String(request)
    });

    if (response?.status === 401) {
      const token = TokenManager.getCachedToken('access_token')?.token;

      // try sign in again
      if (token && TokenManager.isCachedTokenExpired('access_token')) {
        Sentry.logger.info('Token expired, attempting background refresh', {
          log_source: 'token_refresh',
          endpoint: String(request)
        });
        try {
          const refreshed = await refreshTokenIfNeeded('access_token');
          if (refreshed) {
            Sentry.logger.info('Token successfully refreshed', {
              log_source: 'token_refresh',
              endpoint: String(request)
            });
            return;
          } else {
            Sentry.logger.warn('Token refresh failed, redirecting to login', {
              log_source: 'token_refresh',
              endpoint: String(request)
            });
            localStorage.clear();
            router.push('/unauthorized');
            return;
          }
        } catch (err) {
          Sentry.logger.error('Token refresh threw error, redirecting to login', {
            log_source: 'token_refresh',
            endpoint: String(request),
            error: String(err)
          });
          localStorage.clear();
          router.push('/unauthorized');
          return;
        }
      }
    }

    if (response?.status === 403) {
      router.push('/forbidden');
    }
  },

  retry: 3,
  retryDelay: 500, // ms
  retryStatusCodes: [401] // response status codes to retry
});

const mxcApiBaseURL: string = getConfig('VITE_APP_MXC_API_BASE_URL');

export const $mxcFetch = ofetch.create({
  baseURL: mxcApiBaseURL,
  async onRequest({ options }) {
    options.headers = {
      ...options.headers,
      Authorization: 'Basic LKJDNFLKAJFNLKAJSNDF'
    };
  },
  async onResponseError({ request, options, error, response }) {
    captureApiError({
      errorName: '[$mxcFetch]: API response error',
      request,
      response,
      error,
      options,
      method: options?.method,
      endpoint: String(request)
    });
  }
});

const mxsApiBaseURL: string = getConfig('VITE_SECURITY_APP_API_BASE_URL');

export const $mxsFetch = ofetch.create({
  baseURL: mxsApiBaseURL
});

export const $powerBiFetch = ofetch.create({
  baseURL: 'https://api.powerbi.com/v1.0/myorg',
  async onRequest({ options }) {
    const storedToken = TokenManager.getCachedToken('power_bi_embed_access_token');

    options.headers = {
      ...options.headers,
      Authorization: `Bearer ${storedToken?.token}`,
      'Content-Type': 'application/json'
    };
  },

  async onResponseError({ request, options, error, response }) {
    captureApiError({
      errorName: '[$powerBiFetch]: API response error',
      request,
      response,
      error,
      options,
      method: options?.method,
      endpoint: String(request)
    });

    if (response.status === 401) {
      Sentry.logger.warn('Unauthorized Power BI request', {
        log_source: 'power_bi_fetch_error',
        endpoint: String(request),
        method: options?.method || 'GET'
      });
      router.push('/unauthorized');
    }

    if (response.status === 403) {
      Sentry.logger.warn('Forbidden Power BI request', {
        log_source: 'power_bi_fetch_error',
        endpoint: String(request),
        method: options?.method || 'GET'
      });
      router.push('/forbidden');
    }
  }
});
